"use client";

import { useRef } from "react";
import { gsap, useGSAP } from "@/lib/gsap";
import { blurReveal, dotToPill, fadeUp, reduceMotion } from "@/lib/motion";
import { TextReveal } from "@/components/motion/TextReveal";
import { Button } from "@/components/ui/Button";
import { Eyebrow } from "@/components/ui/Eyebrow";
import { ProductMark } from "@/components/ui/ProductMark";
import { DarkPanel } from "@/components/sections/DarkPanel";
import { QSG, QSG_HERO } from "@/content/quiksigns";
import { SignMock } from "./SignMock";

const GRADIENT = "linear-gradient(90deg, #ffffff 0%, #f5f5f5 40%, #9a9aa0 100%)";

/**
 * QuikSigns hero. Same opening cadence as the other product pages: label, headline words
 * de-blur, copy, CTAs grow from dots, and the skills snapshot rises beside it.
 */
export function ProductHero() {
  const root = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const el = root.current;
      if (!el || reduceMotion()) return;
      const q = (s: string) => gsap.utils.toArray<HTMLElement>(s, el);
      const tl = gsap.timeline({ delay: 0.15 });
      tl.fromTo(el.querySelector("[data-dark-panel]"), { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: 1.1, ease: "silk" })
        .add(fadeUp(q("[data-qsg-eyebrow]")), 0.25)
        .add(blurReveal(q("#qsg-title [data-tr-unit]"), { stagger: 0.05 }), 0.35)
        .add(fadeUp(q("[data-qsg-sub]")), 0.75)
        .fromTo(q("[data-qsg-mock]"), { opacity: 0, y: 60, scale: 0.96 }, { opacity: 1, y: 0, scale: 1, duration: 1.4, ease: "silk" }, 0.6);
      q("[data-qsg-cta] [data-dot]").forEach((b, i) => dotToPill(b, tl, 0.95 + i * 0.12));
    },
    { scope: root },
  );

  return (
    <div ref={root} className="pt-[calc(var(--nav-h)+10px)]">
      <DarkPanel
        enter={false}
        ariaLabelledby="qsg-title"
        threads={[10, 22, 88]}
        panelClassName="pb-[clamp(48px,6vw,80px)] pt-[clamp(56px,7vw,100px)]"
      >
        <div className="mx-auto grid w-[min(1180px,calc(100%-2*var(--pad)))] grid-cols-1 items-center gap-12 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] lg:gap-14">
          <div>
            <div data-qsg-eyebrow="" data-reveal-init="">
              <ProductMark productKey="signs" className="mb-4 text-white" />
              <Eyebrow tone="dark">{QSG_HERO.eyebrow}</Eyebrow>
            </div>
            <TextReveal
              as="h1"
              id="qsg-title"
              mode="manual"
              text={QSG_HERO.title}
              gradient={GRADIENT}
              className="mt-5 text-[clamp(1.6rem,3.7vw,3.1rem)] font-[450] leading-[1.06] tracking-[-0.042em]"
            />
            <p data-qsg-sub="" data-reveal-init="" className="mt-5 max-w-[36rem] text-[15px] leading-[1.6] text-white/60">
              {QSG_HERO.sub}
            </p>
            <p data-qsg-sub="" data-reveal-init="" className="mt-4 max-w-[36rem] text-[14px] leading-[1.6] text-white/45">
              {QSG_HERO.lead}
            </p>
            <div data-qsg-cta="" className="mt-8 flex flex-wrap gap-2">
              <Button variant="mint" dot href={QSG.links.register} arrow="inline">
                Start free
              </Button>
              <Button variant="ghost" dot href="#demo" data-intent="demo">
                Book a personalized demo
              </Button>
            </div>
            <p data-qsg-sub="" data-reveal-init="" className="mt-5 text-[14px] text-white/50">
              {QSG_HERO.foot}{" "}
              <a href={QSG_HERO.link.href} className="font-medium text-white underline-offset-4 hover:underline">
                {QSG_HERO.link.label} →
              </a>
            </p>
          </div>
          <div data-qsg-mock="" data-reveal-init="">
            <SignMock />
          </div>
        </div>
      </DarkPanel>
    </div>
  );
}
